import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { ExternalLink, FolderPlus, Link2, Loader2, Unlink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ClientDriveLink } from '@/types/firebase.types'

interface Props {
  clientUserId: string
  clientName: string
  drive: ClientDriveLink | null | undefined
  loading: boolean
  onProvision: (uid: string) => Promise<unknown>
  onLink: (uid: string, folderUrlOrId: string) => Promise<unknown>
  onUnlink: (uid: string) => Promise<void>
}

type Mode = null | 'provision' | 'link' | 'unlink'

export function ClientDriveCell({
  clientUserId,
  clientName,
  drive,
  loading,
  onProvision,
  onLink,
  onUnlink,
}: Props) {
  const [mode, setMode] = useState<Mode>(null)
  const [busy, setBusy] = useState(false)
  const [linkInput, setLinkInput] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  function stop(e: React.MouseEvent) {
    e.stopPropagation()
  }

  function closeDialog() {
    if (busy) return
    setMode(null)
    setErrorMsg(null)
    setLinkInput('')
  }

  async function handleProvision() {
    setErrorMsg(null)
    setBusy(true)
    try {
      await onProvision(clientUserId)
      setMode(null)
    } catch (err) {
      setErrorMsg(formatUserFacingError(err, 'Failed to provision folder'))
    } finally {
      setBusy(false)
    }
  }

  async function handleLink(e: React.FormEvent) {
    e.preventDefault()
    setErrorMsg(null)
    setBusy(true)
    try {
      await onLink(clientUserId, linkInput)
      setMode(null)
      setLinkInput('')
    } catch (err) {
      setErrorMsg(formatUserFacingError(err, 'Failed to link folder'))
    } finally {
      setBusy(false)
    }
  }

  async function handleUnlink() {
    setErrorMsg(null)
    setBusy(true)
    try {
      await onUnlink(clientUserId)
      setMode(null)
    } catch (err) {
      setErrorMsg(formatUserFacingError(err, 'Failed to unlink'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="flex items-center gap-2" onClick={stop}>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : drive?.folderId ? (
          <>
            <a
              href={drive.folderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-[180px] truncate"
              title={drive.folderName}
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate">{drive.folderName}</span>
            </a>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                stop(e)
                setMode('unlink')
              }}
              title="Unlink folder"
            >
              <Unlink className="h-3 w-3" />
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={(e) => {
                stop(e)
                setMode('provision')
              }}
            >
              <FolderPlus className="h-3 w-3 mr-1" />
              Provision
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={(e) => {
                stop(e)
                setMode('link')
              }}
            >
              <Link2 className="h-3 w-3 mr-1" />
              Link
            </Button>
          </>
        )}
      </div>

      {/* Provision confirmation */}
      <Dialog open={mode === 'provision'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent onClick={stop}>
          <DialogHeader>
            <DialogTitle>Provision Google Drive Folder</DialogTitle>
            <DialogDescription>
              Create a new folder for <strong>{clientName}</strong> inside the clients
              shared drive? The folder will be named
              <span className="font-mono"> "{clientName} (email)"</span> and linked to
              this client.
            </DialogDescription>
          </DialogHeader>
          {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleProvision} disabled={busy}>
              {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Create folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual link */}
      <Dialog open={mode === 'link'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent onClick={stop}>
          <DialogHeader>
            <DialogTitle>Link Existing Google Drive Folder</DialogTitle>
            <DialogDescription>
              Link an existing folder to <strong>{clientName}</strong>. Paste the
              folder URL or ID. The folder must be inside the clients shared drive and
              accessible to the impersonated Workspace user.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLink} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="folderInput">Folder URL or ID</Label>
              <Input
                id="folderInput"
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/..."
                autoFocus
                disabled={busy}
              />
            </div>
            {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={closeDialog} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !linkInput.trim()}>
                {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Link folder
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unlink confirmation */}
      <Dialog open={mode === 'unlink'} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent onClick={stop}>
          <DialogHeader>
            <DialogTitle>Unlink Drive Folder</DialogTitle>
            <DialogDescription>
              Remove the link to <strong>{drive?.folderName ?? 'this folder'}</strong>{' '}
              from <strong>{clientName}</strong>? The folder itself will not be deleted
              from Google Drive.
            </DialogDescription>
          </DialogHeader>
          {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleUnlink} disabled={busy}>
              {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
